// Controlled show vocabulary (v0.5). The old compile-time `SHOWS as const` was
// both the value list AND the `Show` type; v0.5 widens `Show` to `string` and
// makes the option list dynamic:
//
//   options = defaults ∪ custom (persisted, per-device) ∪ shows already on booths
//
// Adds are a CONTROLLED action ("+ Add show…"), never free-typing into the field —
// this preserves the February WWIN/WINN typo fix (a mistyped show splits a booth's
// de-dup key). Custom shows persist to localStorage; per-device is fine (a custom
// show also propagates implicitly via any booth captured under it → the
// booth-shows leg of the union).

import { SHOWS } from '../config';

const KEY = 'bt-custom-shows';
const LAST_SHOW_KEY = 'bt-last-show';

/** Read persisted custom shows (this device). Tolerates storage being blocked. */
export function getCustomShows(): string[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === 'string') : [];
  } catch {
    return [];
  }
}

function saveCustomShows(list: string[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* storage blocked (private mode) — session-only is acceptable */
  }
}

/** Case-insensitive dedupe that preserves first-seen order. */
function dedupe(names: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of names) {
    const name = (raw ?? '').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

/**
 * The full option list: defaults, then persisted custom, then any shows already
 * present on local booths (pass them in — this module stays DB-free). Deduped
 * case-insensitively, stable order (defaults first).
 */
export function getShows(boothShows: string[] = []): string[] {
  return dedupe([...SHOWS, ...getCustomShows(), ...boothShows]);
}

/**
 * v1.1 sticky Show: the show used on the last CAPTURE save, per device. Same
 * localStorage-with-a-try/catch shape as the custom-show list above (private mode
 * blocks storage — a device that can't remember just behaves like a new install).
 *
 * Read by the Capture screen only. The Detail screen always shows the booth's own
 * show, and a save there deliberately does NOT write this: editing an old MAGIC
 * booth from the hotel room shouldn't re-point tomorrow's capture default.
 */
export function getLastShow(): string | null {
  try {
    const v = localStorage.getItem(LAST_SHOW_KEY);
    return v && v.trim() ? v : null;
  } catch {
    return null;
  }
}

export function setLastShow(name: string): void {
  const v = (name ?? '').trim();
  if (!v) return;
  try {
    localStorage.setItem(LAST_SHOW_KEY, v);
  } catch {
    /* storage blocked (private mode) — session-only is acceptable */
  }
}

export interface AddShowResult {
  ok: boolean;
  /** Reason when !ok, for a toast. */
  error?: string;
  /** Normalised name that was added (or that already existed). */
  name?: string;
}

/**
 * Validate + persist a new custom show. Rejects empty and case-insensitive
 * duplicates of ANY existing option (default, custom, or booth-seen). Callers
 * pass the current booth-seen shows so the dup check covers them too.
 */
export function addShow(raw: string, boothShows: string[] = []): AddShowResult {
  const name = (raw ?? '').trim();
  if (!name) return { ok: false, error: 'Show name can’t be empty' };
  const exists = getShows(boothShows).some((s) => s.toLowerCase() === name.toLowerCase());
  if (exists) return { ok: false, error: `“${name}” already exists` };
  saveCustomShows([...getCustomShows(), name]);
  return { ok: true, name };
}
